import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, MapPin, ChevronDown, ChevronUp, Database } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { trailsAPI } from '../../utils/api';
import { ImageUploader } from './ImageUploader';

const emptyTrail = {
  name: '',
  distance: '',
  difficulty: 'Easy',
  duration: '',
  type: 'Paved',
  highlights: '',
  description: '',
  amenities: [''],
  coordinates: { lat: 45.0215, lng: -74.7302 },
  startPoint: '',
  image: '',
  order: 0,
  active: true
};

export const TrailsManager = () => {
  const { toast } = useToast();
  const [trails, setTrails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newTrail, setNewTrail] = useState(emptyTrail);
  const [editForm, setEditForm] = useState(emptyTrail);
  const [expandedId, setExpandedId] = useState(null);

  const fetchTrails = useCallback(async () => {
    try {
      const response = await trailsAPI.getAllAdmin();
      setTrails(response.data);
    } catch (error) {
      console.error('Error fetching trails:', error);
      toast({ title: "Error", description: "Failed to fetch trails", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchTrails();
  }, [fetchTrails]);

  const handleSeedTrails = async () => {
    try {
      const response = await trailsAPI.seed();
      toast({ title: "Success", description: response.data.message });
      fetchTrails();
    } catch (error) {
      toast({ title: "Error", description: "Failed to seed trails", variant: "destructive" });
    }
  };

  const handleCreate = async () => {
    if (!newTrail.name || !newTrail.description || !newTrail.image) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    try {
      const trailData = {
        ...newTrail,
        amenities: newTrail.amenities.filter(a => a.trim() !== '')
      };
      await trailsAPI.create(trailData);
      toast({ title: "Trail Created", description: "New trail has been added successfully." });
      setNewTrail(emptyTrail);
      setShowAddForm(false);
      fetchTrails();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create trail", variant: "destructive" });
    }
  };

  const handleUpdate = async (id) => {
    try {
      const updateData = {
        ...editForm,
        amenities: editForm.amenities.filter(a => a.trim() !== '')
      };
      await trailsAPI.update(id, updateData);
      toast({ title: "Trail Updated", description: "Trail has been updated successfully." });
      setEditingId(null);
      fetchTrails();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update trail", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this trail?')) return;
    try {
      await trailsAPI.delete(id);
      toast({ title: "Trail Deleted", description: "Trail has been removed successfully." });
      fetchTrails();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete trail", variant: "destructive" });
    }
  };

  const startEdit = (trail) => {
    setEditingId(trail.id);
    setEditForm({
      ...trail,
      amenities: trail.amenities?.length > 0 ? trail.amenities : ['']
    });
  };

  const addAmenity = (isEdit = false) => {
    if (isEdit) {
      setEditForm({ ...editForm, amenities: [...editForm.amenities, ''] });
    } else {
      setNewTrail({ ...newTrail, amenities: [...newTrail.amenities, ''] });
    }
  };

  const updateAmenity = (index, value, isEdit = false) => {
    if (isEdit) {
      const updated = [...editForm.amenities];
      updated[index] = value;
      setEditForm({ ...editForm, amenities: updated });
    } else {
      const updated = [...newTrail.amenities];
      updated[index] = value;
      setNewTrail({ ...newTrail, amenities: updated });
    }
  };

  const removeAmenity = (index, isEdit = false) => {
    if (isEdit) {
      setEditForm({ ...editForm, amenities: editForm.amenities.filter((_, i) => i !== index) });
    } else {
      setNewTrail({ ...newTrail, amenities: newTrail.amenities.filter((_, i) => i !== index) });
    }
  };

  const TrailForm = ({ data, setData, isEdit = false }) => (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Trail Name *</label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => setData({ ...data, name: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
            placeholder="Cornwall Waterfront Trail"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Distance</label>
          <input
            type="text"
            value={data.distance}
            onChange={(e) => setData({ ...data, distance: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
            placeholder="12 km"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Difficulty</label>
          <select
            value={data.difficulty}
            onChange={(e) => setData({ ...data, difficulty: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          >
            <option value="Easy">Easy</option>
            <option value="Easy to Moderate">Easy to Moderate</option>
            <option value="Moderate">Moderate</option>
            <option value="Moderate to Difficult">Moderate to Difficult</option>
            <option value="Difficult">Difficult</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Duration</label>
          <input
            type="text"
            value={data.duration}
            onChange={(e) => setData({ ...data, duration: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
            placeholder="2-3 hours"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Trail Type</label>
          <select
            value={data.type}
            onChange={(e) => setData({ ...data, type: e.target.value })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          >
            <option value="Paved">Paved</option>
            <option value="Paved Multi-use">Paved Multi-use</option>
            <option value="Natural Surface">Natural Surface</option>
            <option value="Natural Surface & Paved">Natural Surface & Paved</option>
            <option value="Gravel">Gravel</option>
          </select>
        </div>
      </div>

      <ImageUploader
        value={data.image}
        onChange={(url) => setData({ ...data, image: url })}
        label="Trail Image *"
        placeholder="https://images.unsplash.com/..."
      />

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Highlights</label>
        <input
          type="text"
          value={data.highlights}
          onChange={(e) => setData({ ...data, highlights: e.target.value })}
          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          placeholder="Scenic views, wildlife, picnic areas..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
        <textarea
          value={data.description}
          onChange={(e) => setData({ ...data, description: e.target.value })}
          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          rows={4}
          placeholder="Detailed description of the trail..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Starting Point</label>
        <input
          type="text"
          value={data.startPoint}
          onChange={(e) => setData({ ...data, startPoint: e.target.value })}
          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          placeholder="Lamoureux Park, 100 Water St E, Cornwall"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Latitude</label>
          <input
            type="number"
            step="0.0001"
            value={data.coordinates?.lat || 45.0215}
            onChange={(e) => setData({ ...data, coordinates: { ...data.coordinates, lat: parseFloat(e.target.value) } })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Longitude</label>
          <input
            type="number"
            step="0.0001"
            value={data.coordinates?.lng || -74.7302}
            onChange={(e) => setData({ ...data, coordinates: { ...data.coordinates, lng: parseFloat(e.target.value) } })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
      </div>

      {/* Amenities */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Amenities</label>
        {data.amenities.map((amenity, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={amenity}
              onChange={(e) => updateAmenity(index, e.target.value, isEdit)}
              className="flex-1 px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
              placeholder="Free parking, Washrooms, etc."
            />
            {data.amenities.length > 1 && (
              <Button variant="outline" onClick={() => removeAmenity(index, isEdit)} className="text-red-600">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
        <Button variant="outline" onClick={() => addAmenity(isEdit)} className="mt-2">
          <Plus className="w-4 h-4 mr-2" /> Add Amenity
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Display Order</label>
          <input
            type="number"
            value={data.order}
            onChange={(e) => setData({ ...data, order: parseInt(e.target.value) || 0 })}
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
        <div className="flex items-center pt-8">
          <input
            type="checkbox"
            id={`active-${isEdit ? 'edit' : 'new'}`}
            checked={data.active}
            onChange={(e) => setData({ ...data, active: e.target.checked })}
            className="w-4 h-4 mr-2"
          />
          <label htmlFor={`active-${isEdit ? 'edit' : 'new'}`} className="text-sm font-medium text-slate-700">
            Active (visible on site)
          </label>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <div className="text-center py-10">Loading trails...</div>;
  }

  return (
    <div className="space-y-6" data-testid="trails-manager">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Trail Maps Management</h2>
          <p className="text-slate-600">Manage trail information displayed on the Trail Maps page</p>
        </div>
        <div className="flex gap-2">
          {trails.length === 0 && (
            <Button onClick={handleSeedTrails} variant="outline" className="border-blue-300 text-blue-600">
              <Database className="w-4 h-4 mr-2" /> Seed Default Trails
            </Button>
          )}
          <Button onClick={() => setShowAddForm(!showAddForm)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Trail
          </Button>
        </div>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <Card className="border-2 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-600" />
              Add New Trail
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <TrailForm data={newTrail} setData={setNewTrail} />
            <div className="flex gap-4 mt-6">
              <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Save className="w-4 h-4 mr-2" /> Save Trail
              </Button>
              <Button variant="outline" onClick={() => { setShowAddForm(false); setNewTrail(emptyTrail); }}>
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trails List */}
      <div className="space-y-4">
        {trails.length === 0 ? (
          <Card className="border-2 border-slate-200">
            <CardContent className="py-10 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No trails found. Add your first trail or seed default data.</p>
            </CardContent>
          </Card>
        ) : (
          trails.map((trail) => (
            <Card key={trail.id} className={`border-2 ${!trail.active ? 'border-slate-200 bg-slate-50' : 'border-emerald-200'}`}>
              {editingId === trail.id ? (
                <CardContent className="pt-6">
                  <TrailForm data={editForm} setData={setEditForm} isEdit />
                  <div className="flex gap-4 mt-6">
                    <Button onClick={() => handleUpdate(trail.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Save className="w-4 h-4 mr-2" /> Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setEditingId(null)}>
                      <X className="w-4 h-4 mr-2" /> Cancel
                    </Button>
                  </div>
                </CardContent>
              ) : (
                <div className="flex">
                  <div className="w-40 h-32 flex-shrink-0">
                    <img src={trail.image} alt={trail.name} className="w-full h-full object-cover rounded-l-lg" 
                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400'; }} />
                  </div>
                  <CardContent className="flex-1 py-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-bold text-slate-900">{trail.name}</h3>
                          {!trail.active && <Badge variant="outline" className="text-slate-500">Inactive</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <Badge className="bg-green-100 text-green-700">{trail.difficulty}</Badge>
                          <Badge variant="outline">{trail.distance}</Badge>
                          <Badge variant="outline">{trail.duration}</Badge>
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-2">{trail.description}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" onClick={() => setExpandedId(expandedId === trail.id ? null : trail.id)}>
                          {expandedId === trail.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                        <Button variant="outline" onClick={() => startEdit(trail)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" onClick={() => handleDelete(trail.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {expandedId === trail.id && (
                      <div className="mt-4 pt-4 border-t text-sm text-slate-600 space-y-2">
                        <p><strong>Start:</strong> {trail.startPoint}</p>
                        <p><strong>Highlights:</strong> {trail.highlights}</p>
                        <p><strong>Amenities:</strong> {trail.amenities?.join(', ')}</p>
                        <p><strong>Coordinates:</strong> {trail.coordinates?.lat}, {trail.coordinates?.lng}</p>
                      </div>
                    )}
                  </CardContent>
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default TrailsManager;
